import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { API, formatApiError } from "@/lib/api";

const ACCEPT = "image/jpeg,image/png,image/webp,image/gif,application/pdf,text/csv,text/plain";

export function FileUpload({ scope = "attachment", projectId, taskId, accept = ACCEPT, label = "Upload file", onUploaded, testId }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const pick = () => inputRef.current?.click();

  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("scope", scope);
      if (projectId) fd.append("project_id", projectId);
      if (taskId) fd.append("task_id", taskId);
      const token = localStorage.getItem("hf_token");
      const res = await fetch(`${API}/uploads`, { method: "POST", headers: { Authorization: `Bearer ${token}` }, body: fd });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(formatApiError(err.detail) || "Upload failed");
      }
      const data = await res.json();
      toast.success("Uploaded");
      onUploaded?.(data);
    } catch (err) {
      toast.error(err.message || "Upload failed");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <>
      <input ref={inputRef} type="file" accept={accept} className="hidden" onChange={handle} data-testid={testId ? `${testId}-input` : undefined} />
      <Button type="button" variant="outline" onClick={pick} disabled={busy}
        className="rounded-full border-[#e4d8c4] text-[#3f6670]" data-testid={testId}>
        {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
        {busy ? "Uploading…" : label}
      </Button>
    </>
  );
}

// Authenticated <img> for non-public files (fetch as blob)
export function AuthImage({ fileId, alt = "", className = "" }) {
  const [src, setSrc] = useState(null);
  React.useEffect(() => {
    let url;
    const token = localStorage.getItem("hf_token");
    fetch(`${API}/files/${fileId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.blob() : Promise.reject()))
      .then((b) => { url = URL.createObjectURL(b); setSrc(url); })
      .catch(() => {});
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [fileId]);
  if (!src) return <div className={`bg-[#efe6d6] ${className}`} />;
  return <img src={src} alt={alt} className={className} />;
}
